import { useEffect, useRef } from 'react';
import type { Helix2000Character } from '../../shared/CharacterTypes';
import { EventBus, EVENTS } from '../../shared/EventBus';
import { CharacterService } from '../services/CharacterService';
import { seamlessTravel } from '../services/travel/SeamlessTravel';
import { isSeamlessPortalsEnabled } from '../services/travel/seamlessPortalsFlag';

interface TravelSceneHost {
  sceneRegistry: {
    stop(key: string): void;
    start(key: string, data?: unknown): Promise<void>;
  };
}

/**
 * Give the in-place portal trip (plan 25) the world half it needs: stop the
 * current GameScene and start one on the destination map, with the same start
 * data Game.tsx boots the first scene with.
 */
export function useSeamlessTravelScene(
  gameInstance: TravelSceneHost | null,
  player: Helix2000Character | null,
  authToken: string | null,
): void {
  const playerRef = useRef(player);
  playerRef.current = player;

  useEffect(() => {
    if (!gameInstance) return undefined;
    if (isSeamlessPortalsEnabled()) seamlessTravel.armBackNavigation();
    return seamlessTravel.registerScene({
      stop: () => gameInstance.sceneRegistry.stop('GameScene'),
      start: async ({ levelId, spawnId }) => {
        const current = playerRef.current;
        if (!current) throw new Error('no character to place in the destination scene');
        // SceneRegistry.start logs and swallows a failed create(); a ready
        // event is the proof the destination world actually came up.
        let readyScene: { player: Helix2000Character | null } | null = null;
        const off = EventBus.getInstance().on(EVENTS.GAME_SCENE_READY, (scene) => {
          readyScene = scene as { player: Helix2000Character | null };
        });
        try {
          await gameInstance.sceneRegistry.start('GameScene', {
            token: authToken,
            characterId: current._id,
            characterData: CharacterService.getInstance().convertGameToDBCharacter(current),
            levelId,
            spawnId,
            playerClass: current.class,
          });
        } finally {
          off();
        }
        if (!readyScene) throw new Error(`GameScene for ${levelId} did not become ready`);
        // Same hand-off Game.tsx makes after the first scene: the engine binds
        // the app's player object, so its runtime id follows the new session
        // (the "Teleported to" line and other is-it-me checks read it).
        (readyScene as { player: Helix2000Character | null }).player = playerRef.current;
      },
    });
  }, [gameInstance, authToken]);
}
